<template>
    <div class="relative min-h-[500px]">
        <div v-if="loading" class="loading-overlay">
            <div class="loading-content">
                <v-progress-circular indeterminate color="#a11128" size="48"></v-progress-circular>
                <span class="mt-3 text-body-1">Loading...</span>
            </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
            <div class="pb-2 pt-8 flex items-center gap-4">
                <span>Last updated: {{ lastUpdateTime }}</span>
                <v-btn icon="mdi-refresh" size="small" variant="text" :loading="isRefreshing" @click="refreshData"
                    title="Refresh"></v-btn>
            </div>
            <div class="map-wrapper">
                <div id="map"
                    class="h-[500px] bg-gray-200 rounded-2xl flex items-center justify-center text-gray-600 text-xl">
                </div>
            </div>
            <div class="space-y-4 overflow-y-auto max-h-[500px] pr-2">
                <v-container>
                    <v-row>
                        <v-col v-for="event in formattedEvents" :key="event.event_num"
                            @click="selectIncident(event.event_num, event.event_type)" cols="12" md="4">
                            <v-card variant="outlined"
                                :style="{ backgroundColor: cardColourByAlarm[event.alarm_lev] || '#9e2135', color: textColourByAlarm[event.alarm_lev] || '#dea061' }">
                                <v-card-title class="text-wrap"><b>{{ getEventTypeIcon(event) }} {{
                                    event.event_type }}</b></v-card-title>
                                <v-card-text class="py-1 pb-6 text-body-2">
                                    <div>{{ event.formattedStreets.primary }}</div>
                                    <div v-if="event.formattedStreets.secondary" class="text-sm opacity-75 mb-2">
                                        {{ event.formattedStreets.secondary }}
                                    </div>
                                    <div v-else class="mb-2">&nbsp;</div>

                                    <div v-if="event.formattedStreets.district" class="text-xs opacity-60">
                                        <i>({{ event.formattedStreets.district }})</i>
                                    </div>
                                    <div v-else>&nbsp;</div>
                                </v-card-text>
                                <v-card-text class="py-1 text-body-2">
                                    Alarm Level: {{ event.alarm_lev }} | Area: {{ event.beat }}
                                </v-card-text>

                                <div class="d-flex align-center justify-center">
                                    <v-card-actions>
                                        <v-btn
                                            :icon="expandedCards[event.event_num] ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                                            @click="toggleCardExpansion(event.event_num, event.event_type)"></v-btn>
                                    </v-card-actions>
                                </div>

                                <v-expand-transition>
                                    <div v-show="expandedCards[event.event_num]">
                                        <v-divider></v-divider>
                                        <v-card-text>
                                            <v-row class="p-4">
                                                <v-col cols="auto" class="text-left">
                                                    <div class="py-1 text-body-2"><b>#️ Incident:</b></div>
                                                    <div class="py-1 text-body-2"><b>⏰ Dispatched:</b></div>
                                                    <div class="py-1 text-body-2"><b>🚒 Units:</b></div>
                                                </v-col>
                                                <v-col class="text-left">
                                                    <div class="py-1 text-body-2">{{ event.event_num }}</div>
                                                    <div class="py-1 text-body-2">{{ event.dispatch_time.replace("T", " ")}}</div>
                                                    <div class="py-1 text-body-2">{{ formatUnitsDispatched(event) }}
                                                    </div>
                                                </v-col>
                                            </v-row>
                                        </v-card-text>
                                    </div>
                                </v-expand-transition>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useHead } from '@vueuse/head'
import AmazonLocation from 'amazon-location-helpers'
import axios from 'axios'
import _ from 'lodash'
import { drawPoints } from 'maplibre-gl-js-amplify'
import { trackButtonClick, trackMapInteraction, trackEvent } from '../utils/analytics.js'
import { formatStreets } from '../utils/addressFormatting.js'

useHead({
    title: 'Toronto Fire Active Incidents',
    meta: [
        {
            name: 'description',
            content: 'Toronto Fire Active Incidents: Live map, dashboard, detailed analytics & data for firefighting enthusiasts.'
        }
    ]
})

const identityPoolId = process.env.VUE_APP_IDENTITY_POOL_ID;
const mapName = process.env.VUE_APP_MAP_NAME;
const serviceURL = process.env.VUE_APP_SERVICE_URL;
const apiKey = process.env.VUE_APP_API_KEY;

const events = ref([])
const expandedCards = ref({})
const lastUpdateTime = ref('')
const mapRef = ref(null)
const selectedId = ref(null)
const loading = ref(true)
const isRefreshing = ref(false)

const formattedEvents = computed(() => {
    return events.value.map(event => ({
        ...event,
        formattedStreets: formatStreets(event)
    }))
})

const cardColourByAlarm = ref({
    0: '#ffffff',
    1: '#ffffff',
    2: '#ffe645',
    3: '#ffe645',
    4: '#dea061',
})
const textColourByAlarm = ref({
    0: '#2c3e50',
    1: '#2c3e50',
    2: '#2c3e50',
    3: '#2c3e50',
    4: '#a11128',
})
const formatUnitsDispatched = (event) => {
    return event.units_disp ? event.units_disp : 'N/A'
}
const getEventTypeIcon = (event) => {
    const event_type = event.event_type.toLowerCase()
    if (event_type.includes('fire')) {
        return '🔥'
    } else if (event_type.includes('alarm')) {
        return '🚨'
    } else if (event_type.includes('vehicle')) {
        return '🚗'
    } else if (event_type.includes('medical')) {
        return '⚕️'
    } else if (event_type.includes('wire')) {
        return '⚡'
    } else {
        return ''
    }
}

const mapCreate = async () => {
    try {
        // Configure canvas context to avoid getImageData warnings.
        const originalGetContext = HTMLCanvasElement.prototype.getContext;
        HTMLCanvasElement.prototype.getContext = function (type, attributes = {}) {
            if (type === '2d') {
                attributes.willReadFrequently = true;
            }
            return originalGetContext.call(this, type, attributes);
        };

        const apiResponse = await axios.get(serviceURL, {
            headers: {
                'x-api-key': apiKey
            }
        })

        const data = apiResponse.data
        lastUpdateTime.value = data.lastUpdateTime

        const processedEvents = _.map(data.events, event => {
            event.units_disp = _.join(event.units_disp, ', ')
            return _.omit(event, '_id')
        })

        events.value = processedEvents

        const locations = _.chain(data.events)
            .filter(event => !_.isEmpty(event.coordinates))
            .map(event => {
                let address = ''
                const primeStreet = event.prime_street.trim()
                const crossStreets = event.cross_streets.trim()

                if (_.isEmpty(primeStreet)) {
                    const crossStreetList = crossStreets.split('/')
                    address = `${crossStreetList[0]} & ${crossStreetList[1]}`
                } else if (primeStreet.match(/^[A-Za-z]{1}[0-9]{1}[A-Za-z]{1}/)) {
                    address = primeStreet // Postal Code
                } else {
                    address = `${primeStreet} & ${crossStreets}`
                }

                return {
                    coordinates: event.coordinates,
                    title: `${event.event_num} - ${event.event_type}`,
                    address
                }
            })
            .value()

        const map = await AmazonLocation.createMap(
            { identityPoolId },
            {
                container: 'map',
                center: [-79.3832, 43.6532], // Toronto
                zoom: 10,
                style: mapName,
                hash: true
            }
        )

        mapRef.value = map

        map.on('click', (e) => {
            trackMapInteraction('map_click', {
                coordinates: { lat: e.lngLat.lat, lng: e.lngLat.lng },
                zoom_level: map.getZoom()
            })
        })

        map.on('moveend', () => {
            trackMapInteraction('pan', {
                center: map.getCenter(),
                bounds: map.getBounds()
            })
        })

        map.on('zoom', () => {
            trackMapInteraction('zoom', {
                zoom_level: map.getZoom(),
                center: map.getCenter()
            })
        })

        await new Promise((resolve, reject) => {
            const loadHandler = () => {
                try {
                    drawPoints(
                        'mySourceName', // Arbitrary source name
                        locations,
                        map,
                        {
                            showCluster: false,
                            unclusteredOptions: {
                                showMarkerPopup: true
                            },
                            clusterOptions: {
                                showCount: true
                            }
                        }
                    )
                    resolve()
                } catch (error) {
                    reject(error)
                }
            }

            map.on('load', loadHandler)

            map.on('error', (error) => {
                reject(error)
            })
        })

    } catch (error) {
        console.error('Error in map creation:', error)
        // TODO: Notify user of error?
    }
}

const selectIncident = (eventNum, eventType) => {
    selectedId.value = eventNum;

    // Track incident selection
    trackEvent('incident_selected', {
        event_number: eventNum,
        event_type: eventType
    });
};

const toggleCardExpansion = (eventNum, eventType) => {
    const isExpanding = !expandedCards.value[eventNum];
    expandedCards.value[eventNum] = isExpanding;

    // Track card expansion/collapse
    trackEvent('incident_card_toggle', {
        event_number: eventNum,
        event_type: eventType,
        action: isExpanding ? 'expand' : 'collapse'
    });
};

const refreshData = async () => {
    trackButtonClick('refresh_data', 'map_pane');

    isRefreshing.value = true;
    try {
        await mapCreate();
    } catch (error) {
        console.error('Error refreshing data:', error);
        trackEvent('data_refresh_error', {
            error_message: error.message
        });
    } finally {
        isRefreshing.value = false;
        loading.value = false;
    }
};

onMounted(async () => {
    try {
        await mapCreate();
    } catch (error) {
        console.error('Error during component mount:', error);
    } finally {
        loading.value = false;
    }
})

// Clean up when component unmounts
onUnmounted(() => {
    if (mapRef.value) {
        mapRef.value.remove();
    }
})
</script>

<style scoped>
.loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: white;
    padding: 2rem;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 20;
}

.mapPane {
    padding: 5rem;
}

#map {
    z-index: 0;
    width: 100%;
    height: 40vh;
    min-height: 250px;
    max-height: 500px;
    margin-bottom: 2rem;
    border: 1px solid #ccc;
    border-radius: 8px;
}

.map-wrapper {
    width: 100%;
    margin: 0 auto;
    padding: 0 1rem;
}

@media (min-width: 768px) {
    .map-wrapper {
        width: 90%;
        max-width: 1000px;
        padding: 0;
    }
}
</style>